import { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import { getResponseData } from "../../../utils/apiHelpers.js";
import { GetTeamGoalsReportApi } from "../../../api/request/salesDashboard/reports.js";
import {
  GetAllOffices,
  GetAllTeamLeaders,
} from "../../../api/request/subadmin.js";
import {
  GetSalesAgentsApi,
  buildSalesAgentsParams,
  mapSalesAgentsToOptions,
} from "../../../api/request/salesDashboard/dashboard.js";
import DashboardFilters from "../dashboard/components/DashboardFilters.jsx";
import { getTeamGoalsReportColumns } from "../../../columns/salesDashboard/reports/teamGoalsReportColumns.jsx";
import { useUserStore } from "../../../store/userStore.js";

const formatMoney = (value) => `$${Number(value || 0).toLocaleString("en-US")}`;
const formatNumber = (value, digits = 2) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: digits });

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ),
  ).toString();

const TeamGoalsReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useUserStore((state) => state.userDetails);
  const isSales = userDetails?.department === "Sales";
  const isAgent = isSales && !userDetails?.isTeamLeader;
  const isTeamLeader = isSales && userDetails?.isTeamLeader;
  const teamLeadId = isSales ? userDetails?.userId : undefined;
  const [offices, setOffices] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    from: undefined,
    to: undefined,
    period: "Daily",
    officeId: undefined,
    teamLeadId: teamLeadId,
    agentId: undefined,
    status: "All",
    topCards: 5,
  });
  const [topCards, setTopCards] = useState([]);
  const [teamMetrics, setTeamMetrics] = useState([]);

  const fetchOffices = async () => {
    try {
      const data = getResponseData(await GetAllOffices());
      if (data?.success) {
        setOffices(
          (data?.data || []).map((item) => ({
            label: item?.officeName || item?.name,
            value: item?.id || item?.officeId,
          })),
        );
      }
    } catch (error) {
      setOffices([]);
    }
  };

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQuery(filters);
      const data = getResponseData(await GetTeamGoalsReportApi(query));
      if (data?.success) {
        setTopCards(data?.data?.topCards || []);
        setTeamMetrics(data?.data?.teamMetrics || []);
      } else {
        setTopCards([]);
        setTeamMetrics([]);
      }
    } catch (error) {
      setTopCards([]);
      setTeamMetrics([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchTeamLeaders = async () => {
      if (!filters.officeId) {
        setTeamLeaders([]);
        return;
      }
      try {
        const data = getResponseData(await GetAllTeamLeaders(filters.officeId));
        if (data?.success) {
          setTeamLeaders(
            (data?.data || []).map((item) => ({
              label: item?.fullName || item?.teamLeadName,
              value: item?.userId || item?.teamLeadId,
            })),
          );
        }
      } catch (error) {
        setTeamLeaders([]);
      }
    };

    fetchTeamLeaders();
  }, [filters.officeId]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!filters.teamLeadId) {
        setAgents([]);
        return;
      }
      try {
        const data = getResponseData(
          await GetSalesAgentsApi(buildSalesAgentsParams(filters)),
        );
        if (data?.success) {
          setAgents(mapSalesAgentsToOptions(data?.data));
        }
      } catch (error) {
        setAgents([]);
      }
    };

    fetchAgents();
  }, [filters.teamLeadId, filters.agentId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const maxDeposit = Math.max(
    0,
    ...topCards.map((item) => Number(item?.deposits || 0)),
  );

  const columns = useMemo(
    () => getTeamGoalsReportColumns({ formatMoney, formatNumber }),
    [formatMoney, formatNumber],
  );

  return (
    <div className="space-y-4">
      <DashboardFilters
        isSales={isSales}
        isTeamLeader={isTeamLeader}
        isAgent={isAgent}
        filters={filters}
        setFilters={setFilters}
        offices={offices}
        teamLeaders={teamLeaders}
        agents={agents}
      />

      {isLoading ? (
        <div className="flex justify-center flex-col my-20">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {(topCards || []).map((item, idx) => {
              const ratio =
                maxDeposit > 0
                  ? (Number(item?.deposits || 0) / maxDeposit) * 100
                  : 0;
              return (
                <div
                  key={`${item?.teamLeaderId || item?.teamName || idx}`}
                  className="border-2 border-light rounded-xl p-4 overflow-hidden"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-title truncate">
                        {item?.teamName || "--"}
                      </p>
                      <p className="text-description text-sm truncate">
                        Leader - {item?.leaderName || "--"}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full font-semibold">
                      {formatNumber(item?.conversionRatio)}% conv
                    </span>
                  </div>
                  <p className="text-title text-3xl font-semibold mt-3">
                    {formatMoney(item?.deposits)}
                  </p>
                  <p className="text-description text-sm mt-1">
                    Deposits this period
                  </p>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-700"
                      style={{ width: `${Math.max(ratio, 4)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-2 border-light rounded-xl p-4 overflow-hidden">
            <p className="text-title font-semibold mb-3">Team Metrics</p>
            <div className="overflow-hidden rounded-xl">
              <TableWithSkeleton
                showIndexColumn={false}
                pagination={false}
                rowKey="teamLeaderId"
                columns={columns}
                data={teamMetrics}
                loading={isLoading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamGoalsReport;
